import { ProjectDetailFragment, Render_Types_Enum } from '@/gql/generated/graphql'
import { TokenDisplay } from '@components/token-display'
import { getInvocationFromTokenId, getNonLiveDisplayType, SoloViewerToken } from '@utils/common'
import { BodyText, SquareIcon, SquaresFourIcon } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { motion } from 'motion/react'
import { createContext, useContext, useEffect, useState } from 'react'
import { isOob } from '@components/solo-viewer/token-solo-viewer'
import { PaginationButton, PaginationDirection } from '@components/pagination-button'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { TinyGallery } from './tiny-gallery'
import SwitchToggle from '@components/switch-toggle'
import { cn } from '@/lib/utils'
import ToggleButtons from '@components/toggle-buttons'
import { useUserDisplay } from '@/hooks/use-user-display'
import { getProjectStatus, ValidStatusType } from '@utils/project'

type ProjectTokensDisplayProps = {
  project: ProjectDetailFragment
  onExpand: ({ tokens, currentToken }: { tokens: SoloViewerToken[]; currentToken: SoloViewerToken }) => void
}

type ProjectTokensDisplayContext = ProjectTokensDisplayProps & {
  view: ProjectTokensDisplayView
  setView: (view: ProjectTokensDisplayView) => void
  gridDisplayAvailable: boolean
  setGridDisplayAvailable: (available: boolean) => void
  carouselDisplayAvailable: boolean
  setCarouselDisplayAvailable: (available: boolean) => void
}

enum ProjectTokensDisplayView {
  Carousel = 'carousel',
  Grid = 'grid'
}

const ProjectTokensDisplayContext = createContext<ProjectTokensDisplayContext | undefined>(undefined)

function useProjectTokensDisplay() {
  const context = useContext(ProjectTokensDisplayContext)
  if (!context) {
    throw new Error('useProjectTokensDisplay must be used within a ProjectTokensDisplayProvider')
  }
  return context
}

/**
 *
 * This component serves as a context provider for displaying project tokens in either a carousel or grid view.
 * It manages the shared state for view type, display availability, and expansion functionality.
 *
 * @component
 * @example
 * <ProjectTokensDisplay project={project} disableLiveView={false} onExpand={handleExpand}>
 *   <ProjectTokensCarouselDisplay />
 *   <ProjectTokensGridDisplay />
 * </ProjectTokensDisplay>
 *
 * @param {Object} props - The component props
 * @param {ProjectDetailFragment} props.project - The project details to display
 * @param {Function} props.onExpand - Callback function when a token is expanded
 * @param {React.ReactNode} props.children - Child components to render within the context
 *
 * @returns {React.ReactElement} The wrapped child components with context
 *
 * @remarks
 * This component is designed to be used with one or both of the display components:
 * ProjectTokensCarouselDisplay and ProjectTokensGridDisplay. These child components
 * can be used independently and styled separately while sharing the same context.
 * The context includes state for managing which display types are available,
 * allowing for dynamic rendering of view toggle buttons. Toggles to switch between
 * the two view are available if both views are rendered.
 */
export function ProjectTokensDisplay({
  project,
  onExpand,
  children
}: ProjectTokensDisplayProps & { children: React.ReactNode }): React.ReactElement {
  const [view, setView] = useState<ProjectTokensDisplayView>(ProjectTokensDisplayView.Carousel)
  const [gridDisplayAvailable, setGridDisplayAvailable] = useState(false)
  const [carouselDisplayAvailable, setCarouselDisplayAvailable] = useState(false)

  return (
    <ProjectTokensDisplayContext.Provider
      value={{
        project,
        onExpand,
        view,
        setView,
        gridDisplayAvailable,
        setGridDisplayAvailable,
        carouselDisplayAvailable,
        setCarouselDisplayAvailable
      }}
    >
      {children}
    </ProjectTokensDisplayContext.Provider>
  )
}

export function ProjectTokensCarouselDisplay({
  disableLiveView,
  className,
  crunched
}: {
  disableLiveView?: boolean
  className?: string
  crunched?: boolean
}): React.ReactElement | null {
  const { project, view, setView, gridDisplayAvailable, setCarouselDisplayAvailable, onExpand } =
    useProjectTokensDisplay()
  const status = getProjectStatus(project)

  useEffect(() => {
    setCarouselDisplayAvailable(true)
  }, [setCarouselDisplayAvailable])

  const tokens = [
    ...project.tokens.map((t) => ({ ...t, project })),
    ...(status !== ValidStatusType.complete && status !== ValidStatusType.paused
      ? project.oob_tokens.map((t) => ({ ...t, project }))
      : [])
  ].slice(0, 8)

  const [currTokenIndex, setCurrTokenIndex] = useState(0)
  const [showLiveView, setShowLiveView] = useState(project.primary_render_type === Render_Types_Enum.Live)
  const onPaginate = (direction: PaginationDirection) => {
    if (direction === PaginationDirection.Left) {
      setCurrTokenIndex((prev) => (prev === 0 ? tokens.length - 1 : prev - 1))
    } else {
      setCurrTokenIndex((prev) => (prev === tokens.length - 1 ? 0 : prev + 1))
    }
  }

  // TODO: type this better so typescript actually knows this might be undefined
  const currToken: (typeof tokens)[number] | undefined = tokens[currTokenIndex]
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0

  if (view !== ProjectTokensDisplayView.Carousel || !currToken) {
    return null
  }

  return (
    <>
      <motion.div
        key={`${project.name}-token-display-${windowWidth > 1024 ? 'lg' : 'sm'}`}
        className={cn(
          `bg-surface-low relative flex h-[70vh] w-full flex-col items-center justify-center lg:h-[80vh]`,
          className
        )}
      >
        <div className="gap-x-md p-sm flex w-full justify-between md:justify-end">
          <SwitchToggle
            leftLabel="Live view"
            switchedRight={showLiveView}
            onSwitchChange={() => {
              setShowLiveView(!showLiveView)
            }}
            className="md:w-1/2 md:justify-end"
          />
          {gridDisplayAvailable ? (
            <ToggleButtons
              options={[ProjectTokensDisplayView.Carousel, ProjectTokensDisplayView.Grid]}
              optionsContent={[
                <SquareIcon className="w-sm h-sm fill-inherit" key={ProjectTokensDisplayView.Carousel} />,
                <SquaresFourIcon className="w-sm h-sm fill-inherit" key={ProjectTokensDisplayView.Grid} />
              ]}
              selectedOption={view}
              onOptionClick={(view: string) => {
                setView(view as ProjectTokensDisplayView)
              }}
            />
          ) : null}
        </div>
        <div className="px-md md:px-2xl relative w-full flex-1">
          <div className="relative h-full w-full">
            <div className="absolute inset-0">
              <TokenDisplay
                token={currToken}
                project={project}
                preferredDisplayType={
                  showLiveView && !disableLiveView ? Render_Types_Enum.Live : getNonLiveDisplayType(project, currToken)
                }
                loadingScreen={false}
                autoPlay={true}
                onClick={() => onExpand({ tokens: tokens, currentToken: currToken })}
              />
            </div>
          </div>
        </div>
        <motion.div
          className={cn(
            `gap-x-sm text-on-surface p-sm flex w-full flex-row-reverse items-center justify-center md:justify-between`,
            crunched ? 'lg:justify-center xl:justify-between' : ''
          )}
        >
          <TinyGallery
            tokens={tokens}
            currentTokenIndex={currTokenIndex}
            onTokenClick={(newIndex) => setCurrTokenIndex(newIndex)}
            className={cn(
              'hidden w-full justify-center md:flex md:w-1/2 md:justify-end',
              crunched ? 'lg:hidden xl:flex' : ''
            )}
          />
          <button onClick={() => onExpand({ tokens, currentToken: currToken })}>
            <TokenCaption token={currToken} project={project} />
          </button>
        </motion.div>

        {tokens.length > 1 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-between overflow-hidden">
            <motion.div key="left" layout="size" className="pointer-events-auto">
              <PaginationButton
                className="bg-primary-inverted-10 fill-on-primary-inverted p-0"
                direction={PaginationDirection.Left}
                onClick={() => {
                  onPaginate(PaginationDirection.Left)
                  trackButtonClicked(TrackedButtonNames.paginateLeft, {
                    token: isOob(currToken) ? undefined : currToken,
                    oobToken: isOob(currToken) ? currToken : undefined,
                    project
                  })
                }}
              />
            </motion.div>
            <motion.div key="right" layout="size" className="pointer-events-auto">
              <PaginationButton
                className="bg-primary-inverted-10 fill-on-primary-inverted"
                direction={PaginationDirection.Right}
                onClick={() => {
                  onPaginate(PaginationDirection.Right)
                  trackButtonClicked(TrackedButtonNames.paginateRight, {
                    token: isOob(currToken) ? undefined : currToken,
                    oobToken: isOob(currToken) ? currToken : undefined,
                    project
                  })
                }}
              />
            </motion.div>
          </div>
        )}
      </motion.div>
    </>
  )
}

function TokenCaption({
  token,
  project
}: {
  token: SoloViewerToken
  project: ProjectDetailFragment
}): React.ReactElement | null {
  const isOobToken = isOob(token)
  const tokenText = `${project.name} ${isOob(token) ? 'sample' : `#${getInvocationFromTokenId(token.token_id)}`}`

  const { displayName: ownerDisplayName } = useUserDisplay({
    name: isOobToken ? undefined : token.owner?.profile_by_id?.name,
    username: isOobToken ? undefined : token.owner?.profile_by_id?.username,
    address: isOobToken ? undefined : token.owner_address
  })

  const ownerText = isOobToken ? null : `, owned by ${ownerDisplayName}`

  return (
    <BodyText className="text-on-surface-variant w-full text-left hover:underline">
      <span className="italic">{tokenText}</span>
      {ownerText ? <span>{ownerText}</span> : null}
    </BodyText>
  )
}

export function ProjectTokensGridDisplay({ className }: { className?: string }): React.ReactElement | null {
  const { project, onExpand, view, setView, carouselDisplayAvailable, setGridDisplayAvailable } =
    useProjectTokensDisplay()
  const status = getProjectStatus(project)
  useEffect(() => {
    setGridDisplayAvailable(true)
  }, [setGridDisplayAvailable])

  const tokens = [
    ...project.tokens.map((t) => ({ ...t, project })),
    ...(status !== ValidStatusType.complete && status !== ValidStatusType.paused
      ? project.oob_tokens.map((t) => ({ ...t, project }))
      : [])
  ].slice(0, 8)

  if (view !== ProjectTokensDisplayView.Grid) {
    return null
  }

  return (
    <>
      <motion.div className={cn(`bg-surface-low relative flex h-fit w-full flex-col items-center`, className)}>
        <div className="gap-x-md p-sm flex w-full justify-end">
          {carouselDisplayAvailable ? (
            <ToggleButtons
              options={[ProjectTokensDisplayView.Carousel, ProjectTokensDisplayView.Grid]}
              optionsContent={[
                <SquareIcon className="w-sm h-sm fill-inherit" key={ProjectTokensDisplayView.Carousel} />,
                <SquaresFourIcon className="w-sm h-sm fill-inherit" key={ProjectTokensDisplayView.Grid} />
              ]}
              selectedOption={view}
              onOptionClick={(view: string) => {
                setView(view as ProjectTokensDisplayView)
              }}
            />
          ) : null}
        </div>

        <motion.div
          className="relative h-full w-full"
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
        >
          <motion.div className="gap-xs px-xl pb-sm relative grid h-full w-full grid-cols-2">
            {tokens.map((token) => (
              <button
                onClick={() => onExpand({ tokens: tokens, currentToken: token })}
                key={token.token_id}
                className="relative h-fit w-full"
              >
                <ImageWithCustomLoader
                  src={token.media_url ?? ''}
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                    width: '100%',
                    height: 'auto'
                  }}
                  priority
                  alt={`${token.token_id}`}
                  sizes="33vw"
                  blurHash={token.image?.metadata?.blurhash}
                  width={0}
                  height={0}
                  className="w-full"
                />
              </button>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>
    </>
  )
}
